import { useEffect, useState } from "react";
import { UniversalPageLayout, Button } from "../../components/BaseComponents";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import CompanyProjectCreateModal from "./CompanyProjectCreateModal";
import { centsToCurrency, getCSRFToken } from "../../lib/utils";
import routes from "../../components/Router/routes";
import CompanyProjectEditModal from "./CompanyProjectEditModal";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import useCurrencyFormatter from "../../hooks/useCurrencyFormatter";
import useApi from "../../hooks/useApi";
import { generatePath } from "react-router-dom";
import usePagination from "../../hooks/usePagination";
import useDataSort from "../../hooks/useDataSort";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import useCleanup from "../../hooks/useCleanup";
import { useProjectsListZones } from "./useProjectsListZones";

const GRAND_TOTAL_ID = 'grand-total';

function CompanyProjectsList() {
    const api = useApi();
    const [data, setData] = useState([])
    const [projectSummary, setProjectSummary] = useState<any>(null)
    const [grandTotal, setGrandTotal] = useState<any>(null)
    const [summaryError, setSummaryError] = useState<any>()
    const [scope, setScope] = useState<'' | '1' | '2' | '3'>('')

    const pagination = usePagination();
    const dataSort = useDataSort('name');

    const onDelete = async (projectId: string) =>  {
        await api.delete(`/projects/${projectId}`, {
            _csrf: await getCSRFToken()
        })
        setDeletionCandidate(null);
        loadData();
    }

    const [deletionCandidate, setDeletionCandidate] = useState<any>()

    const [editCandidate, setEditCandidate] = useState<any>(null);

    const formatCurrency = useCurrencyFormatter();
    const formatNumber = Intl.NumberFormat('en-GB').format;

    const columns: TableColumn[] = [
        {
            id: 'name',
            label: 'Project name',
            sortable: true,
            renderer: (key, data) => {
                if (data._isGrandTotal) {
                    return <span></span>
                }
                return <span>{data[key]}</span>
            },
        },
        {
            id: 'code',
            label: 'Project code',
            columnClass: 'font-mono',
            sortable: true,
        },
        {
            id: 'totalTurnoverAmount',
            label: 'Project value',
			renderer: (key, data) => {
                const value = data[key]
				return (
                    <p className='text-white'>
                        {
                            value
                                ? formatCurrency(Number(centsToCurrency(value)))
                                : '-'
                        }
                    </p>
				)
			},
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'purchases_count',
            label: 'Purchases',
            sortable: true,
            renderer: (key, data) => {
                const value = data[key]
                return (
                    <p className='text-white'>
                        {value !== null && value !== undefined ? formatNumber(value) : '-'}
                    </p>
                )
            },
            textAlign: 'right',
        },
        {
            id: 'actions',
            label: 'Actions',
            textAlign: 'center',
            renderer: (key, data) => {
                if(data._isGrandTotal) {
                    return <span></span>
                }
                return (
                    <div className="flex justify-center space-x-2">
                        <Button
                            requiresWrite
                            type="Primary"
                            icon='fa-solid fa-trash'
                            hasLeadingIcon
                            size="sm"
                            isTableRowButton
                            onClick={() => setDeletionCandidate(data)}
                        />
                        <Button
                            requiresWrite
                            type="Primary"
                            icon='fa-solid fa-pen-to-square'
                            hasLeadingIcon
                            size="sm"
                            isTableRowButton
                            onClick={() => setEditCandidate(data)}
                        />
                    </div>
                )
            }
        },
        {
            id: 'id',
            label: 'View / refine data',
            renderer: (key, data) => {
                if(data._isGrandTotal) {
                    return <span></span>
                }
                return (
                    <Button
                        text="View project"
                        type="Primary"
                        size="sm"
                        isLink
                        url={generatePath(routes.companyProjects.path, { projectId: data.id })}
                    />
                )
            }
        }
    ];


    const [createModalOpen, setCreateModelOpen] = useState(false);
    const [loading, setLoading] = useState(true)

    const loadData = useCleanup(() => {
        setLoading(true)
        let cancelled = false;
        const run = async () => {
            const qs = new URLSearchParams({
                ...pagination.params,
                ...dataSort.params,
                scope,
            })
            const result = await api.get('/projects?' + qs);
            if(cancelled) {
                return;
            }
            setLoading(false);
            setData(result.data.rows);
            setProjectSummary(result.data.projectSummary)
            setGrandTotal(result.data.grandTotal)
            setSummaryError(undefined)
            pagination.readMeta(result.data.meta);
        }
        run().catch((err) => {
            if(cancelled) {
                return;
            }
            setLoading(false);
            setSummaryError(err)
        })
        return () => {
            cancelled = true;
        }
    })

    const onCreationModalClose = () => {
        setCreateModelOpen(false);
        loadData();
    }

    const handleScopeChange = (newScope: string) => {
        setScope(newScope as '' | '1' | '2' | '3');
        pagination.onPageChange(1);
    }

    useEffect(() => {
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params, dataSort.params, scope])

    // Grand total is a pinned summary row, always shown last regardless of the active sort.
    const tableData = grandTotal
        ? [
            ...data.map((row: any) => ({ _id: row.id, ...row })),
            {
                _isGrandTotal: true,
                _id: GRAND_TOTAL_ID,
                id: GRAND_TOTAL_ID,
                name: '',
                code: '',
                totalTurnoverAmount: grandTotal.totalTurnoverAmount,
                purchases_count: grandTotal.purchasesCount,
            },
        ]
        : data.map((row: any) => ({ _id: row.id, ...row }));

    const tableWithPagination = (
        <>
            <SimpleSortTable
                data={tableData}
                columns={columns}
                defaultSorting={dataSort.defaultSorting}
                onSortPress={dataSort.onChange}
                isLoading={loading}
                rowClassName={(row) => row._isGrandTotal
                    ? 'bg-charcoal-800 text-charcoal-050'
                    : 'bg-charcoal-700 text-white'}
            />
            <Pagination
                isLoading={loading}
                pagination={pagination.pagination}
                onPageChange={pagination.onPageChange}
            />
        </>
    )

    const zones = useProjectsListZones({
        onCreateClick: () => setCreateModelOpen(true),
        tableComponent: tableWithPagination,
        isLoading: loading,
        projectSummary,
        summaryError,
        scope,
        onScopeChange: handleScopeChange,
    })

    return (
        <>
            <UniversalPageLayout
                documentTitle={routes.companyProjects.name}
                heading={{
                    crumbs: [
                        { label: 'Purchases', route: routes.companyPurchases.path },
                        { label: routes.companyProjects.menuName as string }
                    ],
                }}
                summary={zones.summary}
                tabs={zones.tabs}
                actions={zones.actions}
            >
                {zones.table}
            </UniversalPageLayout>
            <CompanyProjectCreateModal
                open={createModalOpen}
                onClose={onCreationModalClose}
            />
            { editCandidate && (
                <CompanyProjectEditModal
                    open={!!editCandidate}
                    onClose={() => setEditCandidate(null)}
                    projectId={editCandidate.id}
                    projectName={editCandidate.name}
                    projectCode={editCandidate.code}
                    projectValue={editCandidate.totalTurnoverAmount}
                    onEdited={() => loadData()}
                />
            )}
            <ConfirmationModal
                open={!!deletionCandidate}
                onCancel={() => setDeletionCandidate(null)}
                onConfirm={() => deletionCandidate && onDelete(deletionCandidate.id)}
            >
                { deletionCandidate && (
                    <div>
                        <PageHeadings title="Delete project" />
                        <p className="my-8 text-white">Delete project {deletionCandidate.name}</p>
                    </div>
                )}
            </ConfirmationModal>
        </>
    )
}

export default CompanyProjectsList;

